import styled, { keyframes } from 'styled-components'

export const AuthContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, ${({ theme }) => theme.palette.primary.default} 0%, ${({ theme }) => theme.palette.accent.purple} 100%);
  padding: ${({ theme }) => theme.spacing[40]};
`

export const AuthCard = styled.div`
  background: ${({ theme }) => theme.palette.background.default};
  border-radius: ${({ theme }) => theme.border.radius.lg};
  box-shadow: ${({ theme }) => theme.elevation.bottom.lg};
  padding: ${({ theme }) => theme.spacing[80]};
  width: 100%;
  max-width: 400px;
`

export const AuthHeader = styled.div`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing[80]};
`

export const AuthTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.heading.size.xl};
  font-weight: ${({ theme }) => theme.typography.weight.bold};
  color: ${({ theme }) => theme.palette.text.default};
  margin: 0 0 ${({ theme }) => theme.spacing[20]} 0;
`

export const AuthSubtitle = styled.p`
  color: ${({ theme }) => theme.palette.text.support};
  margin: 0;
  font-size: ${({ theme }) => theme.typography.body.size.md};
`

export const AuthForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[40]};
`

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`

export const FormLabel = styled.label`
  font-weight: ${({ theme }) => theme.typography.weight.medium};
  color: ${({ theme }) => theme.palette.text.default};
  margin-bottom: ${({ theme }) => theme.spacing[20]};
  font-size: ${({ theme }) => theme.typography.body.size.sm};
`

export const FormInput = styled.input`
  background: ${({ theme }) => theme.component.inputField.backgroundColor};
  border: ${({ theme }) => theme.component.inputField.border};
  border-radius: ${({ theme }) => theme.component.inputField.borderRadius};
  color: ${({ theme }) => theme.component.inputField.color};
  font-size: ${({ theme }) => theme.component.inputField.fontSize};
  padding: ${({ theme }) => theme.component.inputField.padding};
  transition: all 0.2s ease-in-out;

  &::placeholder {
    color: ${({ theme }) => theme.component.inputField.placeholder.color};
  }

  &:focus {
    border-color: ${({ theme }) => theme.component.inputField.focus.borderColor};
    outline: ${({ theme }) => theme.component.inputField.focus.outline};
    outline-offset: ${({ theme }) => theme.component.inputField.focus.outlineOffset};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.component.inputField.disabled.backgroundColor};
    border-color: ${({ theme }) => theme.component.inputField.disabled.borderColor};
    color: ${({ theme }) => theme.component.inputField.disabled.color};
  }
`

export const AuthButton = styled.button<{ $loading?: boolean }>`
  background: ${({ theme }) => theme.component.button.primary.backgroundColor};
  color: ${({ theme }) => theme.component.button.primary.color};
  border: ${({ theme }) => theme.component.button.primary.border};
  padding: ${({ theme }) => theme.component.button.size.md.padding};
  border-radius: ${({ theme }) => theme.component.button.common.borderRadius};
  font-size: ${({ theme }) => theme.component.button.size.md.fontSize};
  font-weight: ${({ theme }) => theme.component.button.common.fontWeight};
  cursor: pointer;
  transition: ${({ theme }) => theme.component.button.common.transition};
  margin-top: ${({ theme }) => theme.spacing[20]};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.component.button.primary.hover.backgroundColor};
    color: ${({ theme }) => theme.component.button.primary.hover.color};
  }

  &:disabled {
    background: ${({ theme }) => theme.palette.disabled.default};
    cursor: not-allowed;
  }

  ${({ $loading }) => $loading && `
    &::after {
      content: '';
      width: 16px;
      height: 16px;
      margin-left: 8px;
      border: 2px solid transparent;
      border-top: 2px solid currentColor;
      border-radius: 50%;
      display: inline-block;
      animation: ${spin} 1s linear infinite;
    }
  `}
`

export const ErrorMessage = styled.div`
  background: ${({ theme }) => theme.palette.background.critical};
  border: ${({ theme }) => theme.border.width.sm} solid ${({ theme }) => theme.palette.status.critical};
  color: ${({ theme }) => theme.palette.status.critical};
  padding: ${({ theme }) => theme.spacing[30]};
  border-radius: ${({ theme }) => theme.border.radius.md};
  font-size: ${({ theme }) => theme.typography.body.size.sm};
`

export const AuthFooter = styled.div`
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing[60]};
  padding-top: ${({ theme }) => theme.spacing[60]};
  border-top: ${({ theme }) => theme.border.width.sm} solid ${({ theme }) => theme.palette.border.default};
`

export const FooterText = styled.p`
  color: ${({ theme }) => theme.palette.text.support};
  margin: 0;
  font-size: ${({ theme }) => theme.typography.body.size.sm};
`

export const LinkButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.palette.primary.default};
  cursor: pointer;
  text-decoration: underline;
  font-weight: ${({ theme }) => theme.typography.weight.medium};
  font-size: inherit;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.palette.primary.active};
  }

  &:disabled {
    color: ${({ theme }) => theme.palette.disabled.emphasis};
    cursor: not-allowed;
  }
`

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`